import { useState, useEffect } from "react";
import {
  Button,
  Card,
  Empty,
  Flex,
  Input,
  message,
  Pagination,
  Skeleton,
  Tag,
  Typography,
} from "antd";
import { useParams } from "react-router";
import { useSelector } from "react-redux";
import PageHeader from "../../components/PageHeader";
import { useAPI } from "../../hooks/useAPI";
import { getCall, postCall } from "../../constants/apicalls.constant";
import { ENDPOINTS } from "../../constants/endpoints.constant";
import { RootState } from "../../store/store";
import { STATUS } from "../../constants/status.constant";
import { formatDisplayDate } from "../../utils/DateAndTimeConvertor";

const { TextArea } = Input;
const { Text } = Typography;

type ChangeRequest = {
  id: string;
  event_id: string;
  message: string;
  status: string;
  admin_remark: string | null;
  requested_by: string;
  created_at: string;
  updated_at: string;
  requested_by_user?: {
    id: string;
    first_name: string;
    last_name: string;
  };
};

type ListResponse = {
  status: boolean;
  data: {
    data: ChangeRequest[];
    current_page: number;
    per_page: number;
    total: number;
  };
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
};

const getStatusTag = (status: string) => {
  const colors = STATUS[status as keyof typeof STATUS] ?? STATUS.pending;
  return (
    <Tag color={colors.bg} style={{ color: colors.text, marginInlineEnd: 0 }}>
      {STATUS_LABELS[status] ?? status}
    </Tag>
  );
};

const ChangeRequests = () => {
  const { id: paramId } = useParams();
  const event = useSelector((state: RootState) => state.event);
  const eventId = paramId || event?.id;

  const [requestMessage, setRequestMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const {
    response: listResponse,
    loading: listLoading,
    trigger: fetchList,
  } = useAPI<ListResponse>(ENDPOINTS.change_request);

  const { trigger: submitRequest } = useAPI(ENDPOINTS.change_request);

  const loadRequests = () => {
    if (!eventId) return;
    fetchList(
      getCall({
        filter: { event_id: { eq: eventId }, sort: "created_at:desc" },
        event_id: eventId,
        page: currentPage,
        per_page: pageSize,
      }),
    );
  };

  useEffect(() => {
    loadRequests();
  }, [eventId, currentPage]);

  const handleSubmit = async () => {
    if (!requestMessage.trim()) {
      message.warning("Please describe what you need to change");
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitRequest(
        postCall({ event_id: eventId, message: requestMessage.trim() }),
      );
      if (res && (res as { status?: boolean }).status) {
        message.success("Change request submitted successfully");
        setRequestMessage("");
        setCurrentPage(1);
        loadRequests();
      } else {
        message.error(
          (res as { message?: string })?.message ||
            "Failed to submit change request",
        );
      }
    } catch {
      message.error("Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const requests = listResponse?.data?.data || [];
  const total = listResponse?.data?.total || 0;

  return (
    <div style={{ padding: "0 24px 24px" }}>
      <PageHeader
        title="Change Requests"
        backBtn={false}
        description="Request changes to your published event"
        bottomMargin={false}
      />

      <Card
        style={{ marginBottom: 16, marginTop: 16 }}
        title={
          <Text strong style={{ fontSize: 14 }}>
            Submit a New Request
          </Text>
        }
      >
        <TextArea
          rows={3}
          placeholder="Describe what you need to change (e.g., event date, venue, ticket details...)"
          value={requestMessage}
          onChange={(e) => setRequestMessage(e.target.value)}
          maxLength={2000}
          showCount
          style={{ marginBottom: 24 }}
        />
        <Flex justify="flex-end">
          <Button
            type="primary"
            size="small"
            onClick={handleSubmit}
            loading={submitting}
            disabled={!requestMessage.trim()}
          >
            Submit Request
          </Button>
        </Flex>
      </Card>

      <Card
        title={
          <Text strong style={{ fontSize: 14 }}>
            Previous Requests ({total})
          </Text>
        }
      >
        {listLoading ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : requests.length > 0 ? (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {requests.map((req) => (
                <div
                  key={req.id}
                  style={{
                    padding: "12px 16px",
                    border: "1px solid #f0f0f0",
                    borderRadius: 8,
                    background: "#fafafa",
                  }}
                >
                  <Flex
                    justify="space-between"
                    align="flex-start"
                    gap={12}
                    wrap="wrap"
                  >
                    <Text
                      style={{
                        fontSize: 13,
                        flex: 1,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {req.message}
                    </Text>
                    {getStatusTag(req.status)}
                  </Flex>

                  {req.admin_remark && (
                    <div
                      style={{
                        marginTop: 8,
                        padding: "8px 12px",
                        background: "#fff",
                        border: "1px solid #e8e8e8",
                        borderRadius: 6,
                      }}
                    >
                      <Text
                        type="secondary"
                        style={{
                          fontSize: 11,
                          display: "block",
                          marginBottom: 2,
                        }}
                      >
                        Admin Response
                      </Text>
                      <Text style={{ fontSize: 13 }}>{req.admin_remark}</Text>
                    </div>
                  )}

                  <Text
                    type="secondary"
                    style={{ fontSize: 11, display: "block", marginTop: 8 }}
                  >
                    {formatDisplayDate(req.created_at)}
                  </Text>
                </div>
              ))}
            </div>

            {total > pageSize && (
              <div style={{ marginTop: 16, textAlign: "right" }}>
                <Pagination
                  current={listResponse?.data?.current_page ?? currentPage}
                  pageSize={listResponse?.data?.per_page ?? pageSize}
                  total={total}
                  onChange={setCurrentPage}
                  showSizeChanger={false}
                  size="small"
                />
              </div>
            )}
          </>
        ) : (
          <Empty description="No change requests yet" />
        )}
      </Card>
    </div>
  );
};

export default ChangeRequests;
